import { createRouter, createWebHistory } from 'vue-router'
import ChatView from '@/views/ChatView.vue'
import LibraryView from '@/views/LibraryView.vue'
import LawDetails from '@/views/Law-Details.vue'
import MessengerView from '@/views/MessengerView.vue'
import NotificationsView from '@/views/NotificationsView.vue'
import LawyersView from '@/views/LawyersView.vue'
import FirmOfficesView from '@/views/FirmOfficesView.vue'
import SupportView from '@/views/SupportView.vue'
import LoginView from '@/views/Login.vue'
import RegisterView from '@/views/Register.vue'
import { requireAuth, requireGuest } from './guards'

const routes = [
  // Auth routes (guest only)
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: { title: 'Login' },
    beforeEnter: requireGuest
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterView,
    meta: { title: 'Register' },
    beforeEnter: requireGuest
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPassword.vue'),
    meta: { title: 'Forgot Password' },
    beforeEnter: requireGuest
  },
  {
    path: '/reset-password/:token',
    name: 'ResetPassword',
    component: () => import('@/views/ResetPassword.vue'),
    meta: { title: 'Reset Password' },
    beforeEnter: requireGuest
  },

  // Protected routes
  {
    path: '/',
    name: 'Chat',
    component: ChatView,
    meta: { title: 'Legal Assistant', requiresAuth: true },
    beforeEnter: requireAuth
  },
  {
    path: '/library',
    name: 'Library',
    component: LibraryView,
    meta: { title: 'Legal Library', requiresAuth: true },
    beforeEnter: requireAuth
  },
  {
    path: '/law/:id',
    name: 'LawDetails',
    component: LawDetails,
    meta: { title: 'Law Details', requiresAuth: true },
    beforeEnter: requireAuth
  },
  {
    path: '/messenger',
    name: 'Messenger',
    component: MessengerView,
    meta: { title: 'Messages', requiresAuth: true },
    beforeEnter: requireAuth
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: NotificationsView,
    meta: { title: 'Notifications', requiresAuth: true },
    beforeEnter: requireAuth
  },
  {
    path: '/lawyers',
    name: 'Lawyers',
    component: LawyersView,
    meta: { title: 'Lawyers', requiresAuth: true },
    beforeEnter: requireAuth
  },
  {
    path: '/firm-offices',
    name: 'FirmOffices',
    component: FirmOfficesView,
    meta: { title: 'Law Firms', requiresAuth: true },
    beforeEnter: requireAuth
  },
  {
    path: '/support',
    name: 'Support',
    component: SupportView,
    meta: { title: 'Customer Support', requiresAuth: true },
    beforeEnter: requireAuth
  },

  // User profile
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/Profile.vue'),
    meta: { title: 'Profile', requiresAuth: true },
    beforeEnter: requireAuth
  },

  // LiveKit Video Call
  {
    path: '/video-call',
    name: 'VideoCall',
    component: () => import('@/views/VideoCall.vue'),
    meta: { title: 'Video Consultation', requiresAuth: true },
    beforeEnter: requireAuth
  },

  // Unauthorized page
  {
    path: '/unauthorized',
    name: 'Unauthorized',
    component: () => import('@/views/Unauthorized.vue'),
    meta: { title: 'Unauthorized' }
  },

  // Catch all route
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: { title: 'Page Not Found' }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Global navigation guard
router.beforeEach(async (to, _from, next) => {
  // Initialize auth store if not already done
  const authStore = await import('@/stores/auth').then(module => module.useAuthStore())
  
  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/login')
    return
  }

  // Update page title
  if (to.meta.title) {
    document.title = `${to.meta.title} - Legal AI Assistant`
  }

  next()
})

export default router
